// Custom Element to handle  Invoice Functionality
class CartInvoice extends HTMLElement {
  constructor() {
    super();

    this.invoiceCheckbox = null;
    this.invoiceForm = null;
    this.hiddenFields = {};
  }

  connectedCallback() {
    this.initializeElements();
    this.attachEventListeners();
    this.loadSavedState();
  }

  initializeElements() {
    // Get the context from the data attribute
    const container = this.querySelector(".custom--attribute-check");
    this.contextPrefix = container?.dataset.invoiceContext || "";

    // Find the form using the matching data attribute
    this.form = document.querySelector(
      `form[data-invoice-context="${this.contextPrefix}"]`
    );

    if (!this.form) {
      console.error(
        "Cart Invoice: Could not find form with context:",
        this.contextPrefix
      );
      return;
    }

    // Initialize UI elements
    this.invoiceCheckbox = this.querySelector('[id$="InvoiceRequestCheck"]');
    this.invoiceForm = this.querySelector('[id$="InvoiceForm"]');

    // Initialize hidden fields from the form
    this.hiddenFields = {
      partitaIva: this.form.querySelector(
        `[id="${this.contextPrefix}PartitaIva"]`
      ),
      codiceSdiPec: this.form.querySelector(
        `[id="${this.contextPrefix}CodiceSdiPec"]`
      ),
    };
  }

  attachEventListeners() {
    if (this.invoiceCheckbox) {
      this.invoiceCheckbox.addEventListener("change", () =>
        this.handleInvoiceToggle()
      );
    }

    this.setupInputSynchronization();
    this.setupCheckoutValidation();
  }

  handleInvoiceToggle() {
    if (this.invoiceCheckbox.checked) {
      this.invoiceForm.style.display = "block";
      this.saveInvoiceState();
    } else {
      this.invoiceForm.style.display = "none";
      this.clearHiddenFields();
      this.clearSavedState();
      this.clearAllErrorLabels();
    }
  }

  setupInputSynchronization() {
    this.syncInput("VisiblePartitaIva", "partitaIva");
    this.syncInput("VisibleCodiceSdiPec", "codiceSdiPec");
  }

  syncInput(sourcePattern, targetFieldKey) {
    const source = this.querySelector(`[id$="${sourcePattern}"]`);
    const target = this.hiddenFields[targetFieldKey];

    if (source && target) {
      const savedValue = localStorage.getItem(source.id);
      if (savedValue) {
        source.value = savedValue;
        target.value = savedValue;
      }

      source.addEventListener("input", () => {
        target.value = source.value;
        localStorage.setItem(source.id, source.value);
        this.clearErrorLabel(source.id);
      });
    }
  }

  setupCheckoutValidation() {
    if (this.form) {
      this.form.addEventListener("submit", (event) => {
        if (this.invoiceCheckbox && this.invoiceCheckbox.checked) {
          this.syncRelevantHiddenFields();
        } else {
          this.clearHiddenFields();
        }

        this.validateCheckout(this.form, event);
      });
    }
  }

  syncRelevantHiddenFields() {
    this.clearHiddenFields();
    this.syncFieldValue("VisiblePartitaIva", "partitaIva");
    this.syncFieldValue("VisibleCodiceSdiPec", "codiceSdiPec");
  }

  syncFieldValue(sourcePattern, targetField) {
    const source = this.querySelector(`[id$="${sourcePattern}"]`);
    const target = this.hiddenFields[targetField];

    if (source && target && source.value.trim()) {
      target.value = source.value.trim();
    }
  }

  validateCheckout(form, event) {
    const checkoutButton = form.querySelector('button[name="checkout"]');

    if (this.invoiceCheckbox && this.invoiceCheckbox.checked) {
      const isValid = this.validateInvoiceForms();
      if (!isValid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (checkoutButton) {
          checkoutButton.setAttribute("aria-busy", "false");
          checkoutButton.removeAttribute("disabled");
        }
      }
    }
  }

  validateInvoiceForms() {
    this.clearAllErrorLabels();
    let isValid = true;
    let firstInvalidField = null;

    if (!this.validateField("VisiblePartitaIva")) {
      isValid = false;
      firstInvalidField = this.querySelector('[id$="VisiblePartitaIva"]');
    }
    if (!this.validateField("VisibleCodiceSdiPec")) {
      isValid = false;
      firstInvalidField =
        this.querySelector('[id$="VisibleCodiceSdiPec"]') || firstInvalidField;
    }

    if (!isValid && firstInvalidField) {
      firstInvalidField.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalidField.focus();
    }

    return isValid;
  }

  validateField(fieldPattern) {
    const field = this.querySelector(`[id$="${fieldPattern}"]`);
    if (!field) return true;

    const isValid = field.value.trim() !== "";
    if (!isValid) {
      this.showErrorLabel(field.id);
    }
    return isValid;
  }

  loadSavedState() {
    const savedInvoiceRequest = localStorage.getItem("invoiceRequestChecked");

    if (savedInvoiceRequest === "true" && this.invoiceCheckbox) {
      this.invoiceCheckbox.checked = true;
      if (this.invoiceForm) {
        this.invoiceForm.style.display = "block";
      }
    }
  }

  saveInvoiceState() {
    if (this.invoiceCheckbox) {
      localStorage.setItem(
        "invoiceRequestChecked",
        this.invoiceCheckbox.checked
      );
    }
  }

  clearSavedState() {
    localStorage.removeItem("invoiceRequestChecked");
  }

  clearHiddenFields() {
    Object.values(this.hiddenFields).forEach((field) => {
      if (field) field.value = "";
    });
  }

  showErrorLabel(inputId) {
    const input = this.querySelector(`#${inputId}`);
    const label = this.querySelector(`label[for="${inputId}"]`);

    if (input && label) {
      const errorMessage = label.getAttribute("data-title");
      if (errorMessage) {
        label.textContent = errorMessage;
        label.style.display = "block";
        label.style.color = "#ff4444";
        input.style.borderColor = "#ff4444";
      }
    }
  }

  clearErrorLabel(inputId) {
    const input = this.querySelector(`#${inputId}`);
    const label = this.querySelector(`label[for="${inputId}"]`);

    if (input && label) {
      label.textContent = "";
      label.style.display = "none";
      input.style.borderColor = "";
    }
  }

  clearAllErrorLabels() {
    const errorLabels = this.querySelectorAll(
      ".custom-inputs-title-container label"
    );
    errorLabels.forEach((label) => {
      label.textContent = "";
      label.style.display = "none";
    });

    const inputs = this.querySelectorAll(
      ".custom-inputs-title-container input"
    );
    inputs.forEach((input) => {
      input.style.borderColor = "";
    });
  }
}

if (!window.customElements.get("cart-invoice")) {
  window.customElements.define("cart-invoice", CartInvoice);
}

// Custom Element to handle Invoice Agreement Checkbox Validation
class InvoiceAgreement extends HTMLElement {
  constructor() {
    super();
    this.checkbox = null;
    this.errorPopup = null;
    this.errorMessage = null;
    this.form = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.attachEventListeners();
  }

  initializeElements() {
    // Find the checkbox inside this element
    this.checkbox = this.querySelector('input[type="checkbox"]');

    // Find error message elements
    this.errorPopup = this.querySelector(".error-message-popup");
    this.errorMessage = this.querySelector(".error-message");

    // Find the parent form
    this.form = this.closest("form");

    if (!this.checkbox) {
      console.error("Invoice Agreement: Checkbox not found");
      return;
    }

    if (!this.form) {
      console.error("Invoice Agreement: Parent form not found");
      return;
    }
  }

  attachEventListeners() {
    if (!this.checkbox || !this.form) return;

    // Listen for checkbox changes
    this.checkbox.addEventListener("change", () => {
      this.handleCheckboxChange();
    });

    // Listen for checkout button clicks specifically
    const checkoutButton = this.form.querySelector('button[name="checkout"]');
    if (checkoutButton) {
      checkoutButton.addEventListener("click", (e) => {
        if (!this.validateCheckbox()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.showError();
          checkoutButton.setAttribute("aria-busy", "false");
          checkoutButton.removeAttribute("disabled");
        }
      });
    }

    // Also listen for form submission as a backup
    this.form.addEventListener("submit", (e) => {
      this.handleFormSubmit(e);
    });
  }

  handleCheckboxChange() {
    if (this.checkbox.checked) {
      this.hideError();
    } else {
      if (this.errorMessage) {
        this.errorMessage.style.display = "block";
      }
    }
  }

  handleFormSubmit(e) {
    if (!this.validateCheckbox()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.showError();

      // Reset the checkout button's loading state
      const checkoutButton = this.form.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.setAttribute("aria-busy", "false");
        checkoutButton.removeAttribute("disabled");
      }

      return false;
    }
    return true;
  }

  validateCheckbox() {
    return this.checkbox && this.checkbox.checked;
  }

  showError() {
    // Show popup error
    if (this.errorPopup) {
      this.errorPopup.style.display = "flex";
      this.errorPopup.style.opacity = "1";

      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.hideErrorPopup();
      }, 115000);
    }

    // Show inline error message
    if (this.errorMessage) {
      this.errorMessage.style.display = "block";
    }

    // Add visual feedback to checkbox
    if (this.checkbox) {
      this.checkbox.style.outline = "2px solid rgb(220, 38, 38)";
      this.checkbox.style.outlineOffset = "2px";
    }

    // Scroll to the checkbox if it's not visible
    this.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  hideError() {
    this.hideErrorPopup();

    // Hide inline error message
    if (this.errorMessage) {
      this.errorMessage.style.display = "none";
    }

    // Remove visual feedback from checkbox
    if (this.checkbox) {
      this.checkbox.style.outline = "";
      this.checkbox.style.outlineOffset = "";
    }
  }

  hideErrorPopup() {
    if (this.errorPopup) {
      this.errorPopup.style.display = "none";
    }
  }
}

if (!window.customElements.get("invoice-agreement")) {
  window.customElements.define("invoice-agreement", InvoiceAgreement);
}
